import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import Logo from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MessageSquare, ArrowLeft, Heart, Trash2, Plus, BookOpen, Search,
  Loader2, Pin, Tag as TagIcon, Send,
} from "lucide-react";

/* ---------------------------------------------------------------- helpers */
function timeAgo(iso) {  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
function Avatar({ name, role }) {
  const letter = (name || "?").charAt(0).toUpperCase();
  const bg = role === "admin" ? "bg-[#E53935]" : "bg-[#222222]";
  return (
    <div className={`shrink-0 w-9 h-9 rounded-full ${bg} text-white flex items-center justify-center text-sm font-bold`}>
      {letter}
    </div>
  );
}
function RoleChip({ role }) {
  if (role === "admin") {
    return <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E53935] text-white font-bold">Admin</span>;
  }
  return <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Learner</span>;
}

/* =========================================================================
 * Forum landing — list of threads, with search + filters and new-thread CTA.
 * =======================================================================*/
export function ForumIndex() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const courseId = params.get("course") || "";
  const moduleId = params.get("module") || "";
  const [composeOpen, setComposeOpen] = useState(false);
  const user = getUser();

  const load = useCallback(async () => {    try {
      const { data } = await api.get("/forum/threads", { params: {
        q: q || undefined,
        course_id: courseId || undefined,
        module_id: moduleId || undefined,
      }});
      setThreads(data);
    } catch (e) {
      toast.error("Failed to load forum");
    } finally {
      setLoading(false);
    }
  }, [q, courseId, moduleId]);

  useEffect(() => { load(); }, [load]);

  // Forum is internal — dealer accounts are redirected home.
  if (user?.audience === "dealer") return <Navigate to="/learn" replace />;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/learn" className="flex items-center gap-3 text-slate-600 hover:text-[#E53935]" data-testid="forum-back-link">
            <ArrowLeft size={16}/>
            <Logo className="h-8"/>
          </Link>
          <div className="text-sm font-medium text-slate-800 inline-flex items-center gap-2">
            <MessageSquare size={16} className="text-[#E53935]"/> Learner Forum
          </div>
        </div>
      </header>

      {/* Hero / intro */}
      <section className="bg-gradient-to-br from-[#222222] via-[#2a0d0d] to-[#E53935] text-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">Community · Discuss · Clarify</div>
          <h1 className="text-3xl md:text-4xl font-display font-bold leading-tight">Ask. Answer. Get unstuck — together.</h1>
          <p className="mt-2 text-white/75 max-w-xl text-sm md:text-base">
            Tag a module, ask a question, share what worked. Your peers and the team chime in.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => setComposeOpen(true)}
              data-testid="new-thread-button"
              className="bg-white text-[#E53935] hover:bg-slate-100 font-semibold"
            >
              <Plus size={16} className="mr-1.5"/> Start a discussion
            </Button>
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60"/>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search threads…"
                data-testid="forum-search-input"
                className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-white/40"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Threads */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="py-16 text-center text-slate-400 inline-flex items-center gap-2 w-full justify-center">
            <Loader2 size={16} className="animate-spin"/> Loading discussions…
          </div>
        ) : threads.length === 0 ? (
          <Card className="p-12 text-center border-dashed" data-testid="forum-empty">
            <MessageSquare size={32} className="mx-auto text-slate-300 mb-3" strokeWidth={1.4}/>
            <div className="font-medium text-slate-700">No discussions yet</div>
            <div className="text-sm text-slate-500 mt-1">Be the first to ask a question or share a tip.</div>
            <Button
              onClick={() => setComposeOpen(true)}
              className="mt-5 bg-[#E53935] hover:bg-[#c5302d] text-white"
              data-testid="empty-state-new-thread"
            >
              <Plus size={14} className="mr-1.5"/> Start a discussion
            </Button>
          </Card>
        ) : (
          <ul className="space-y-3" data-testid="thread-list">
            {threads.map((t) => <ThreadRow key={t.id} thread={t}/>)}
          </ul>
        )}
      </main>

      <NewThreadDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        defaultCourseId={courseId}
        defaultModuleId={moduleId}
        onCreated={() => { setComposeOpen(false); load(); }}
        user={user}
      />
    </div>
  );
}

function ThreadRow({ thread }) {
  return (
    <li>
      <Link
        to={`/learn/forum/${thread.id}`}
        className="block group"
        data-testid={`thread-row-${thread.id}`}
      >
        <Card className="p-5 hover:border-[#E53935]/40 hover:shadow-md transition-all">
          <div className="flex items-start gap-4">
            <Avatar name={thread.author_name} role={thread.author_role}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {thread.pinned && <Pin size={12} className="text-[#E53935]"/>}
                <h3 className="font-display font-semibold text-slate-900 group-hover:text-[#E53935] transition-colors line-clamp-1">
                  {thread.title}
                </h3>
              </div>
              <p className="text-sm text-slate-600 line-clamp-2 mb-3">{thread.body}</p>
              <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                <span className="font-medium text-slate-700">{thread.author_name}</span>
                <RoleChip role={thread.author_role}/>
                <span>· {timeAgo(thread.last_activity_at || thread.created_at)}</span>
                {(thread.module_title || thread.course_title) && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                    <TagIcon size={10}/>
                    {thread.module_title || thread.course_title}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right shrink-0 text-xs text-slate-500">
              <div className="inline-flex items-center gap-1">
                <MessageSquare size={11}/> {thread.reply_count || 0}
              </div>
              <div className="inline-flex items-center gap-1 mt-1">
                <Heart size={11} className={thread.liked_by_me ? "fill-[#E53935] text-[#E53935]" : ""}/>
                {thread.like_count || 0}
              </div>
            </div>
          </div>
        </Card>
      </Link>
    </li>
  );
}

/* =========================================================================
 * Thread detail — post body + replies + reply composer.
 * =======================================================================*/
export function ForumThread() {
  const { threadId } = useParams();
  const nav = useNavigate();
  const user = getUser();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/forum/threads/${threadId}`);
      setThread(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Thread not found");
        nav("/learn/forum");
      } else {
        toast.error("Failed to load thread");
      }
    } finally {
      setLoading(false);
    }
  }, [threadId, nav]);

  useEffect(() => { load(); }, [load]);

  const isAdmin = user?.role === "admin";
  const myUid = user?.learner_id || user?.email;

  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setPosting(true);
    try {
      await api.post(`/forum/threads/${threadId}/replies`, { body: replyBody.trim() });
      setReplyBody("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post reply");
    } finally {
      setPosting(false);
    }
  };

  const toggleLike = async (kind, id) => {
    try {
      await api.post(`/forum/${kind === "thread" ? "threads" : "replies"}/${id}/like`);
      await load();
    } catch (e) {
      toast.error("Failed to update like");
    }
  };

  const deleteThread = async () => {
    if (!window.confirm("Delete this thread? All replies will be removed.")) return;
    try {
      await api.delete(`/forum/threads/${threadId}`);
      toast.success("Thread deleted");
      nav("/learn/forum");
    } catch (e) {
      toast.error("Failed to delete thread");
    }
  };

  const deleteReply = async (replyId) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/forum/replies/${replyId}`);
      await load();
    } catch (e) {
      toast.error("Failed to delete reply");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400">
        <Loader2 size={20} className="animate-spin"/>
      </div>
    );
  }
  if (!thread) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/learn/forum" className="flex items-center gap-2 text-slate-600 hover:text-[#E53935]" data-testid="back-to-forum-link">
            <ArrowLeft size={16}/> Forum
          </Link>
          <Logo className="h-8"/>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <Card className="p-6" data-testid="thread-detail">
          <div className="flex items-start gap-4">
            <Avatar name={thread.author_name} role={thread.author_role}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {thread.pinned && <Pin size={14} className="text-[#E53935]"/>}
                <h1 className="text-2xl font-display font-bold text-slate-900 leading-tight">{thread.title}</h1>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap mb-4">
                <span className="font-medium text-slate-700">{thread.author_name}</span>
                <RoleChip role={thread.author_role}/>
                <span>· {timeAgo(thread.created_at)}</span>
                {thread.module_title && (
                  <Link
                    to={`/learn/forum?module=${thread.module_id}`}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#E53935]/10 text-[#E53935] rounded hover:bg-[#E53935]/15"
                    data-testid="thread-module-tag"
                  >
                    <TagIcon size={10}/> {thread.module_title}
                  </Link>
                )}
                {!thread.module_title && thread.course_title && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                    <BookOpen size={10}/> {thread.course_title}
                  </span>
                )}
              </div>
              <div className="prose prose-slate prose-sm max-w-none whitespace-pre-wrap leading-relaxed">
                {thread.body}
              </div>
              <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100">
                <button
                  onClick={() => toggleLike("thread", thread.id)}
                  data-testid="thread-like-button"
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    thread.liked_by_me
                      ? "bg-[#E53935]/10 border-[#E53935]/40 text-[#E53935]"
                      : "bg-white border-slate-200 text-slate-600 hover:border-[#E53935]/40 hover:text-[#E53935]"
                  }`}
                >
                  <Heart size={12} className={thread.liked_by_me ? "fill-[#E53935]" : ""}/>
                  {thread.like_count || 0}
                </button>
                <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                  <MessageSquare size={11}/> {thread.reply_count || 0} replies
                </div>
                <div className="ml-auto inline-flex items-center gap-2">
                  {(isAdmin || thread.author_id === myUid) && (
                    <button
                      onClick={deleteThread}
                      data-testid="delete-thread-button"
                      className="text-xs text-slate-400 hover:text-red-600 inline-flex items-center gap-1"
                    >
                      <Trash2 size={11}/> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Replies */}
        <section className="mt-8 space-y-3" data-testid="reply-list">
          {(thread.replies || []).length === 0 && (
            <div className="text-sm text-slate-400 text-center py-6">Be the first to reply.</div>
          )}
          {(thread.replies || []).map((r) => (
            <Card key={r.id} className="p-4" data-testid={`reply-${r.id}`}>
              <div className="flex items-start gap-3">
                <Avatar name={r.author_name} role={r.author_role}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1.5 flex-wrap">
                    <span className="font-medium text-slate-700">{r.author_name}</span>
                    <RoleChip role={r.author_role}/>
                    <span>· {timeAgo(r.created_at)}</span>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{r.body}</div>
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => toggleLike("reply", r.id)}
                      data-testid={`reply-like-${r.id}`}
                      className={`inline-flex items-center gap-1 text-[11px] ${
                        r.liked_by_me ? "text-[#E53935]" : "text-slate-500 hover:text-[#E53935]"
                      }`}
                    >
                      <Heart size={11} className={r.liked_by_me ? "fill-[#E53935]" : ""}/>
                      {r.like_count || 0}
                    </button>
                    {(isAdmin || r.author_id === myUid) && (
                      <button
                        onClick={() => deleteReply(r.id)}
                        data-testid={`delete-reply-${r.id}`}
                        className="text-[11px] text-slate-400 hover:text-red-600 inline-flex items-center gap-1"
                      >
                        <Trash2 size={10}/> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </section>

        {/* Reply composer */}
        <form onSubmit={submitReply} className="mt-8 sticky bottom-4" data-testid="reply-form">
          <Card className="p-3 shadow-lg border-slate-200">
            <Textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder="Write a reply…"
              rows={3}
              data-testid="reply-textarea"
              className="border-0 focus-visible:ring-0 resize-none"
            />
            <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-2">
              <span className="text-[10px] text-slate-400">Be kind. Be specific. Tag the module section if relevant.</span>
              <Button
                type="submit"
                disabled={posting || !replyBody.trim()}
                data-testid="submit-reply-button"
                className="bg-[#E53935] hover:bg-[#c5302d] text-white"
              >
                {posting ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Send size={14} className="mr-1.5"/>}
                Reply
              </Button>
            </div>
          </Card>
        </form>
      </main>
    </div>
  );
}

/* =========================================================================
 * New thread dialog — tag a course/module, then create.
 * =======================================================================*/
function NewThreadDialog({ open, onOpenChange, defaultCourseId, defaultModuleId, onCreated, user }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [courseId, setCourseId] = useState(defaultCourseId || "");
  const [moduleId, setModuleId] = useState(defaultModuleId || "");
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(""); setBody("");
    setCourseId(defaultCourseId || ""); setModuleId(defaultModuleId || "");
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
  }, [open, defaultCourseId, defaultModuleId]);

  useEffect(() => {
    if (!courseId) { setModules([]); return; }
    api.get(`/modules?course_id=${courseId}`).then(({ data }) => setModules(data)).catch(() => setModules([]));
  }, [courseId]);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 4) return toast.error("Title must be at least 4 characters");
    if (!body.trim()) return toast.error("Add some content");
    setPosting(true);
    try {
      const { data } = await api.post("/forum/threads", {
        title: title.trim(),
        body: body.trim(),
        course_id: courseId || null,
        module_id: moduleId || null,
      });
      toast.success("Thread posted");
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Start a discussion</DialogTitle>
          <DialogDescription>
            Ask a question or share what you learned. Tag the course or module to help others find it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="new-thread-form">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. RT60 vs STC — when to use each?"
              data-testid="new-thread-title"
              maxLength={180}
              autoFocus
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Your post</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Share the context, what you tried, and what's confusing…"
              rows={6}
              data-testid="new-thread-body"
              maxLength={8000}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Tag course</label>
              <select
                value={courseId}
                onChange={(e) => { setCourseId(e.target.value); setModuleId(""); }}
                data-testid="new-thread-course-select"
                className="w-full text-sm border border-slate-300 rounded px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
              >
                <option value="">— none —</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Tag module</label>
              <select
                value={moduleId}
                onChange={(e) => setModuleId(e.target.value)}
                disabled={!courseId}
                data-testid="new-thread-module-select"
                className="w-full text-sm border border-slate-300 rounded px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 disabled:bg-slate-50"
              >
                <option value="">— none —</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={posting}
              data-testid="submit-new-thread-button"
              className="bg-[#E53935] hover:bg-[#c5302d] text-white"
            >
              {posting ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Plus size={14} className="mr-1.5"/>}
              Post
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ForumIndex;
